import { inject } from '@angular/core';
import { errorToString } from '@kinet-open-network/shared/util';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { BankAccount } from '../models/bank-account.model';
import { OpenBankingStatus } from '../models/open-banking-status.model';
import { BanksService } from '../services/banks.service';

export interface BankOptionsState {
  accounts: BankAccount[];
  accountsLoading: boolean;
  accountsError: string | null;

  openBankingStatus: OpenBankingStatus | null;
  openBankingStatusLoading: boolean;
  openBankingStatusError: string | null;
}

export const BankOptionsStore = signalStore(
  { protectedState: false },
  withState<BankOptionsState>({
    accounts: [],
    accountsLoading: false,
    accountsError: null,
    openBankingStatus: null,
    openBankingStatusLoading: false,
    openBankingStatusError: null,
  }),
  withMethods((store, banksService = inject(BanksService)) => ({
    loadBankAccounts: async () => {
      patchState(store, { accountsLoading: true, accountsError: null });

      try {
        const accounts = await firstValueFrom(banksService.getAccounts());
        patchState(store, { accounts, accountsLoading: false });
        return accounts;
      } catch (error) {
        patchState(store, {
          accountsLoading: false,
          accountsError: errorToString(error),
        });
        return [];
      }
    },
    setBankAccountAsDefault: async (account: BankAccount) => {
      patchState(store, { accountsLoading: true });

      try {
        const updatedAccount = await firstValueFrom(
          banksService.setDefaultBankAccount(account.id),
        );

        patchState(store, {
          accounts: store
            .accounts()
            .map((a) =>
              a.id === account.id
                ? updatedAccount
                : new BankAccount({ ...a, default: false }),
            ),
        });
      } finally {
        patchState(store, { accountsLoading: false });
      }
    },
    loadOpenBankingStatus: async () => {
      patchState(store, {
        openBankingStatusLoading: true,
        openBankingStatusError: null,
      });

      try {
        const status = await firstValueFrom(
          banksService.getOpenBankingStatus(),
        );
        patchState(store, {
          openBankingStatus: status,
          openBankingStatusLoading: false,
        });
        return status;
      } catch (error) {
        patchState(store, {
          openBankingStatusLoading: false,
          openBankingStatusError: errorToString(error),
        });
        return null;
      }
    },
  })),
);
